/**
 * NOS-265: the ONE public-site-shell loader. Every SPA public content page
 * (blog/events/media/campaign listing + detail, fundraiser share) previously
 * cloned a ~50-60 line org → nav-pages → org_customizations effect with
 * forked SELECT lists — which is how org custom CSS silently never applied on
 * any content subpage (audit bug B4) and how nav order drifted (NOS-258).
 *
 * Pages consume this hook plus <PublicSitePage> (the matching layout) and keep
 * only their own content queries. Rule (CLAUDE.md): pages never call
 * supabase.from() for the shell — this hook is the shell.
 */
import { useEffect, useMemo, useState } from "react"
import { supabase } from "@/lib/supabase"
import { useSiteStyles } from "@/hooks/useGoogleFont"
import { useNavigation } from "@/hooks/useNavigation"
import { sortPages } from "@/stores/customizationStore"
import type { NavItem } from "@/types/navigation"
import type { Organization, OrgCustomizations } from "@/templates/types"

/** Nav-page summary — replaces the 10 per-file NavPageSummary declarations. */
export interface NavPageSummary {
  id: string
  slug: string
  title: string | null
  page_type?: string | null
}

/**
 * Canonical column lists. Superset of every consumer's needs — adding a
 * customization column means editing HERE, not 9 pages (NOS-247 lesson).
 */
export const PUBLIC_ORG_SELECT =
  "id, name, slug, mission_statement, logo_url, custom_domain, podcast_settings, legal_name, mailing_address, founding_year"
export const ORG_CUSTOMIZATIONS_SELECT =
  "primary_color, secondary_color, font_family, heading_font, body_font, font_size, sticky_nav, template_id, template_version, nav_cta_text, custom_css, logo_scale, announcement, design_tokens, footer_content"

const DEFAULT_CUSTOMIZATIONS: OrgCustomizations = {
  primary_color: "#3b82f6",
  secondary_color: "#1e40af",
  font_family: "Inter, sans-serif",
  template_id: null,
}

export interface UsePublicSiteShellOptions {
  /** Pre-fetched org (subdomain flow: PublicNonprofitSitePage passes it). Skips the org query. */
  org?: Organization | null
  /** Pre-fetched customizations. Skips the org_customizations query. */
  customizations?: OrgCustomizations | null
  /** "" on subdomain/custom domain; "/:orgSlug" on path-based routing. */
  basePath?: string
}

export interface PublicSiteShell {
  org: Organization | null
  /** Raw customizations row (null until loaded). */
  customizations: OrgCustomizations | null
  /** Customizations with defaults applied — safe to read directly. */
  cust: OrgCustomizations
  navPages: NavPageSummary[]
  navItems: NavItem[]
  /** Shell (org + customizations) still loading. Content loading is per-page. */
  loading: boolean
  /** Org could not be resolved. */
  notFound: boolean
  basePath: string
  getPagePath: (slug: string) => string
  orgName: string
  primaryColor: string
  secondaryColor: string
  /** Body font — also what the nav/footer use. */
  fontFamily: string
  headingFont: string
  logoScale: number
}

export function usePublicSiteShell(
  orgSlug: string | null | undefined,
  options: UsePublicSiteShellOptions = {}
): PublicSiteShell {
  const { org: orgProp = null, customizations: custProp = null, basePath = "" } = options
  const [orgState, setOrgState] = useState<Organization | null>(orgProp)
  const [custState, setCustState] = useState<OrgCustomizations | null>(custProp)
  const [navPages, setNavPages] = useState<NavPageSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!orgProp && !orgSlug) {
        setNotFound(true)
        setLoading(false)
        return
      }

      let orgRow = orgProp
      if (!orgRow) {
        if (!orgSlug) {
          setNotFound(true)
          setLoading(false)
          return
        }
        const { data, error } = await supabase
          .from("organizations")
          .select(PUBLIC_ORG_SELECT)
          .eq("slug", orgSlug)
          .maybeSingle()
        if (cancelled) return
        if (error || !data) {
          setNotFound(true)
          setLoading(false)
          return
        }
        orgRow = data as unknown as Organization
      }
      if (!cancelled) setOrgState(orgRow)

      const { data: pagesData } = await supabase
        .from("pages")
        .select("id, slug, title, page_type")
        .eq("org_id", orgRow.id)
        .eq("is_published", true)
      if (!cancelled && pagesData) setNavPages(sortPages(pagesData as NavPageSummary[]))

      if (custProp) {
        if (!cancelled) setCustState(custProp)
      } else {
        const { data: custData } = await supabase
          .from("org_customizations")
          .select(ORG_CUSTOMIZATIONS_SELECT)
          .eq("org_id", orgRow.id)
          .maybeSingle()
        if (!cancelled && custData) setCustState(custData as OrgCustomizations)
      }

      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgSlug, orgProp, custProp])

  const org = orgProp ?? orgState
  const customizations = custProp ?? custState
  const cust = customizations ?? DEFAULT_CUSTOMIZATIONS

  const fontFamily = cust.body_font || cust.font_family || "Inter, sans-serif"
  const headingFont = cust.heading_font || fontFamily
  useSiteStyles(headingFont, fontFamily, cust.font_size ?? "medium")

  const { items: navItems } = useNavigation(org?.id ?? null)

  const getPagePath = useMemo(
    () => (slug: string) => (slug === "home" ? basePath || "/" : `${basePath}/${slug}`),
    [basePath]
  )

  return {
    org,
    customizations,
    cust,
    navPages,
    navItems,
    loading,
    notFound,
    basePath,
    getPagePath,
    orgName: org?.name ?? "Organization",
    primaryColor: cust.primary_color ?? "#3b82f6",
    secondaryColor: cust.secondary_color ?? "#1e40af",
    fontFamily,
    headingFont,
    logoScale: (cust.logo_scale as number | undefined) ?? 1.0,
  }
}
